/**
 * @file CatalogElementPanels.tsx
 * @description Панели «Статьи» и «Отзывы» (phase19.16 Р2; uiux-design-r2
 * §4; SB25 — данные только из блога и базы отзывов, вписать текстом
 * нельзя): источник «сами / выбрать», сколько, вид. Выбор вручную —
 * `IdPickField`. «Категории» — `CategoriesPanel.tsx`.
 * SB120 этап 3б (uiux-sb120-stage3 §3 «Сетки остальных видов»): статьи —
 * в ряд 1–6 и на телефоне (необязательные: нет ключа — как было, 3 на ПК и
 * стопкой); отзывы — [Лентой][Сеткой] и в ряд 1–4 (↺ 3).
 * SB122 (uiux-settings-groups §3): подгруппы «Какие …» (источник, сколько,
 * счётчик) и «Вид» / «На карточке» — уровень 2 внутри группы «Содержимое».
 */

import { GRID_PER_ROW, REVIEW_PER_ROW, SECTION_LOOK_LIMITS, SITE_LIMITS } from "@chaiberi/shared";

import { Label } from "../../../ui/label";
import { SettingsSubgroup } from "../../../ui/settings-groups";
import { CheckRow } from "../../../site-view/CheckRow";
import { Segmented } from "../../../site-view/Segmented";
import { DataCountLine } from "../fields/DataCountLine";
import { IdPickField } from "../fields/IdPickField";
import { useSectionForm } from "../section-form-context";
import { GridCountField } from "./GridCountField";
import { PhoneGridField } from "./PhoneGridField";
import type { ElementPanelProps } from "./types";
import { reviewsPerRowHint } from "../../../../lib/site-builder/grid-meta";
import { countOptions as counts } from "../../../../lib/site-builder/section-meta";


export function ArticlesPanel({ element, onChange, path, dataCount, countStale = false }: ElementPanelProps<"articles">) {
  const { issueAt } = useSectionForm();
  const set = (patch: Partial<typeof element>) => onChange({ ...element, ...patch });
  // Необязательные ключи 3б: «как было» — ключ снимается.
  const setOptional = <K extends "perRow" | "phoneLayout">(key: K, v: (typeof element)[K] | undefined) => {
    const next = { ...element };
    if (v === undefined) delete next[key];
    else next[key] = v;
    onChange(next);
  };
  return (
    <>
      <SettingsSubgroup title="Какие статьи">
        <Segmented
          aria-label="Какие статьи"
          options={[
            { value: "latest" as const, label: "Свежие" },
            { value: "manual" as const, label: "Выбрать" },
          ]}
          value={element.source}
          onChange={(source) => set({ source })}
        />
        {element.source === "manual" ? (
          <IdPickField label="Статьи" list="articles" ids={element.ids} max={SITE_LIMITS.articles} addPlaceholder="+ Добавить статью" error={issueAt(`${path}.ids`)} onChange={(ids) => set({ ids })} />
        ) : (
          <>
            <Label>Сколько</Label>
            <Segmented aria-label="Сколько статей" options={counts(SITE_LIMITS.articles)} value={String(element.limit)} onChange={(v) => set({ limit: Number(v) })} />
          </>
        )}
        <DataCountLine count={dataCount} limit={element.source === "manual" ? element.ids.length : element.limit} stale={countStale} />
      </SettingsSubgroup>
      <SettingsSubgroup title="На карточке">
        <div className="flex flex-wrap gap-x-4">
          <CheckRow checked={element.show.category} onChange={(category) => set({ show: { ...element.show, category } })}>Рубрика</CheckRow>
          <CheckRow checked={element.show.author} onChange={(author) => set({ show: { ...element.show, author } })}>Автор</CheckRow>
          <CheckRow checked={element.show.date} onChange={(date) => set({ show: { ...element.show, date } })}>Дата</CheckRow>
        </div>
      </SettingsSubgroup>
      {/* Умолчания статей (3 в ряд, стопкой) — дубль витрины, TD-446. */}
      <SettingsSubgroup title="Вид">
        <GridCountField kind="articles" field="perRow" values={GRID_PER_ROW} value={element.perRow} fallback={3} inheritLabel="как было · 3" path={path} onChange={(perRow) => setOptional("perRow", perRow)} />
        <PhoneGridField ariaLabel="Статьи на телефоне" value={element.phoneLayout ?? "stack"} onChange={(v) => setOptional("phoneLayout", v === "stack" && element.phoneLayout === undefined ? undefined : v)} />
      </SettingsSubgroup>
    </>
  );
}

const RD = SECTION_LOOK_LIMITS.reviewsPerRow.default;

export function ReviewsPanel({ element, onChange, path, dataCount, countStale = false }: ElementPanelProps<"reviews">) {
  const { issueAt } = useSectionForm();
  const set = (patch: Partial<typeof element>) => onChange({ ...element, ...patch });
  return (
    <>
      <SettingsSubgroup title="Какие отзывы">
        <Label>Источник</Label>
        <Segmented
          aria-label="Отзывы о чём"
          options={[
            { value: "product" as const, label: "О товарах" },
            { value: "site" as const, label: "О магазине" },
          ]}
          value={element.source}
          onChange={(source) => set({ source, ids: [] })}
        />
        <Segmented
          aria-label="Какие отзывы"
          options={[
            { value: "best" as const, label: "Лучшие 4–5★" },
            { value: "recent" as const, label: "Свежие" },
            { value: "manual" as const, label: "Выбрать" },
          ]}
          value={element.pick}
          onChange={(pick) => set({ pick })}
        />
        {element.pick === "manual" ? (
          <IdPickField
            label="Отзывы"
            list={element.source === "product" ? "productReviews" : "siteReviews"}
            ids={element.ids}
            max={SITE_LIMITS.manualProducts}
            addPlaceholder="+ Добавить отзыв"
            error={issueAt(`${path}.ids`)}
            onChange={(ids) => set({ ids })}
          />
        ) : (
          <>
            <Label>Сколько</Label>
            <Segmented aria-label="Сколько отзывов" options={counts(12)} value={String(element.limit)} onChange={(v) => set({ limit: Number(v) })} />
          </>
        )}
        <DataCountLine count={dataCount} limit={element.pick === "manual" ? element.ids.length : element.limit} stale={countStale} />
        <p className="type-caption text-on-surface-variant">Тексты отзывов — только из базы отзывов: вписать их здесь нельзя.</p>
      </SettingsSubgroup>
      <SettingsSubgroup title="Вид">
        <Segmented
          aria-label="Вид отзывов"
          options={[
            { value: "carousel" as const, label: "Лентой" },
            { value: "grid" as const, label: "Сеткой" },
          ]}
          value={element.layout}
          onChange={(layout) => set({ layout })}
        />
        <GridCountField
          kind="reviews"
          field="perRow"
          values={REVIEW_PER_ROW}
          value={element.perRow}
          fallback={RD}
          inheritLabel={`как было · ${RD}`}
          path={path}
          hint={element.layout === "carousel" ? reviewsPerRowHint(element.perRow ?? RD) : undefined}
          onChange={(perRow) => {
            const next = { ...element };
            if (perRow === undefined) delete next.perRow;
            else next.perRow = perRow;
            onChange(next);
          }}
        />
      </SettingsSubgroup>
    </>
  );
}
